import axios from "axios";
import { call, put, takeEvery, takeLatest, select } from "redux-saga/effects";
import constants from "constants";
import actions from "actions";
import { toast } from "react-toastify";
import { push } from "redux-router";
import validator from "services/validator.js";
import validate from "validate.js";
import _ from "lodash";
import { transformWebinarResponse } from "../../utils/webinar-response-transformer";

const {
  addRequestError,
  addErrorsSaga,
  addValidateError,
  cleanErrors,
  addClientUrlsAdmin,
  addClientUrlAdmin,
  addClientUrlWebinarsAdmin,
  startRequest,
  endRequest,
} = actions;

const {
  LOAD_CLIENT_URLS_SAGA_ADMIN,
  LOAD_CLIENT_URL_SAGA_ADMIN,
  LOAD_CLIENT_URL_WEBINARS_SAGA_ADMIN,
  CREATE_CLIENT_URL_SAGA,
  EDIT_CLIENT_URL_SAGA,
  DELETE_CLIENT_URL_SAGA,
  API_CLIENT_URLS_ADMIN,
  API_CLIENT_URL_WEBINARS_ADMIN,
} = constants;

const getUrl = (params = {}) => {
  const keys = Object.keys(params);

  return (keys || []).map((key) => `&${key}=${params[key]}`).join("");
};

const apiCreateClientUrl = (client_url) =>
  axios.post(API_CLIENT_URLS_ADMIN, client_url);

const apiUpdateClientUrl = (id, client_url) =>
  axios.put(`${API_CLIENT_URLS_ADMIN}/${id}`, client_url);

const apiLoadAdminClientUrls = (page, query) =>
  axios.get(API_CLIENT_URLS_ADMIN, {
    params: getUrl({
      page,
      per_page: 5,
      ...query,
    }),
  });

const apiLoadAdminClientUrl = (id, params = {}) =>
  axios.get(`${API_CLIENT_URLS_ADMIN}/${id}`, {
    params: {
      ...params,
    },
  });

const apiDeleteClientUrl = (client_url_ids) => {
  return axios.post(`${API_CLIENT_URLS_ADMIN}/destroy`, {
    client_url_ids,
  });
};

const apiLoadAdminClientUrlWebinars = async (id, params = {}) => {
  const response = await axios.get(`${API_CLIENT_URL_WEBINARS_ADMIN}/${id}`, {
    params: {
      ...params,
    },
  });
  return transformWebinarResponse(response);
};

const formatRequestData = (clientUrl) => {
  let data = new FormData();

  Object.keys(clientUrl).forEach((field) => {
    if (field === "webinar_ids") {
      clientUrl[field].forEach((id) => {
        data.append(`client_url[${field}][]`, id);
      });
    } else {
      data.append(`client_url[${field}]`, clientUrl[field]);
    }
  });

  return data;
};

function* createClientUrlSaga({ client_url }) {
  try {
    if (
      yield select((store) => store.requests.indexOf("client_url_create") != -1)
    )
      return;

    yield put(startRequest("client_url_create"));

    const errors = validator.validate(client_url, "clientUrl");
    if (errors) {
      yield put(addValidateError(CREATE_CLIENT_URL_SAGA, errors));
      yield put(endRequest("sponsor_create"));
      return;
    } else {
      yield put(cleanErrors(CREATE_CLIENT_URL_SAGA));
    }
    const clientUrlRequestData = yield call(formatRequestData, client_url);
    const response = yield call(apiCreateClientUrl, clientUrlRequestData);
    const createdClientUrl = response.data.client_url;
    toast.success("Client Url successfully created!");
    yield put(push("/client-url/"));
    yield put(endRequest("client_url_create"));
  } catch (e) {
    yield put(endRequest("client_url_create"));
    yield put(addErrorsSaga(CREATE_CLIENT_URL_SAGA, e.response));
  }
}

function* editClientUrlSaga({ client_url }) {
  try {
    const errors = validator.validate(client_url, "clientUrl");
    if (errors) {
      yield put(addValidateError(EDIT_CLIENT_URL_SAGA, errors));
      return;
    } else {
      yield put(cleanErrors(EDIT_CLIENT_URL_SAGA));
    }

    const clientUrlRequestData = yield call(formatRequestData, client_url);
    const response = yield call(
      apiUpdateClientUrl,
      client_url.id,
      clientUrlRequestData
    );
    toast.success("Client Url successfully updated!");
    yield put(push("/client-url/"));
  } catch (e) {
    yield put(addErrorsSaga(EDIT_CLIENT_URL_SAGA, e.response));
  }
}

function* deleteClientUrlSaga({ client_url_ids }) {
  if (!client_url_ids.length) return;
  const clientUrlIds = client_url_ids;
  try {
    console.log("clientUrlIds", clientUrlIds);
    yield call(apiDeleteClientUrl, clientUrlIds);
    toast.success("Client Url successfully deleted!");
    const query = yield select((state) => state.clientUrls.admin.list.query) ||
      {};
    const page = yield select((state) => state.clientUrls.admin.list.page) || 1;
    yield call(loadAdminClientUrls, { page, query });
  } catch (e) {
    yield put(addErrorsSaga(DELETE_CLIENT_URL_SAGA, e.response));
  }
}

function* loadAdminClientUrls({ page = 1, query = {} }) {
  try {
    const response = yield call(apiLoadAdminClientUrls, page, query);
    const clientUrl = response.data.client_urls;
    const count = response.data.count;
    const totalPages = response.data.total_pages;

    yield put(addClientUrlsAdmin(clientUrl, count, page, totalPages, query));
  } catch (e) {
    yield put(addRequestError(e.response));
  }
}

function* loadAdminClientUrl({ id }) {
  try {
    const response = yield call(apiLoadAdminClientUrl, id);
    const clientUrl = response.data.client_url;

    yield put(addClientUrlAdmin(clientUrl));
  } catch (e) {
    yield put(addRequestError(e.response));
  }
}

function* loadAdminClientUrlWebinars({ id }) {
  try {
    const response = yield call(apiLoadAdminClientUrlWebinars, id);
    const webinars = response.data.webinars;

    yield put(addClientUrlWebinarsAdmin(webinars));
  } catch (e) {
    yield put(addRequestError(e.response));
  }
}

function* __createClientUrlSaga() {
  yield takeEvery(CREATE_CLIENT_URL_SAGA, createClientUrlSaga);
}

function* __editClientUrlSaga() {
  yield takeEvery(EDIT_CLIENT_URL_SAGA, editClientUrlSaga);
}

function* __deleteClientUrlSaga() {
  yield takeEvery(DELETE_CLIENT_URL_SAGA, deleteClientUrlSaga);
}

function* __loadClientUrlsAdminSaga() {
  yield takeEvery(LOAD_CLIENT_URLS_SAGA_ADMIN, loadAdminClientUrls);
}

function* __loadAdminClientUrlSaga() {
  yield takeEvery(LOAD_CLIENT_URL_SAGA_ADMIN, loadAdminClientUrl);
}

function* __loadClientUrlWebinarsSaga() {
  yield takeEvery(
    LOAD_CLIENT_URL_WEBINARS_SAGA_ADMIN,
    loadAdminClientUrlWebinars
  );
}

export {
  __createClientUrlSaga,
  __editClientUrlSaga,
  __deleteClientUrlSaga,
  __loadClientUrlsAdminSaga,
  __loadAdminClientUrlSaga,
  __loadClientUrlWebinarsSaga,
};



// WEBPACK FOOTER //
// ./src/sagas/admin/clientUrls.js